// ---------------------------------------------------------------------------
// Every element a guide is allowed to point at, in one place.
//
// Guides reference ANCHOR.something, never a raw selector string. Pages tag the
// element with the matching data-tour value. When someone renames a button six
// months from now, this file is the thing that shows up in their grep.
//
//     <Button data-tour={ANCHOR_ID.membersAdd}>Add Member</Button>
//     ...
//     { target: ANCHOR.membersAdd }
//
// scripts/check-guides.js asserts that every guide target is a value of ANCHOR
// and that every anchor id is rendered somewhere in src. It cannot assert the
// element is on screen at the moment the step runs - that is the job of the
// Playwright anchor suite (Phase 6) and of the engine's stall telemetry in prod.
// ---------------------------------------------------------------------------

// Raw data-tour values, for the pages that set the attribute.
export const ANCHOR_ID = {
  // Sidebar nav (set via the `tourId` prop in NavConfig, rendered by NavItem)
  //
  // Only top-level group items are anchored. A child sits inside a collapsed
  // accordion until its parent is open, so a step pointing at one would stall on
  // a perfectly healthy page. Persona guides route to child pages instead and
  // let the engine navigate.
  navDashboard: 'tour-nav-dashboard',
  navFrontDesk: 'tour-nav-frontdesk',
  navMembers: 'tour-nav-members',
  navSpaces: 'tour-nav-spaces',
  navLeads: 'tour-nav-leads',
  navCommerce: 'tour-nav-commerce',
  navReports: 'tour-nav-reports',
  navAssets: 'tour-nav-assets',
  navExpenses: 'tour-nav-expenses',
  navInspect: 'tour-nav-inspect',

  // Dashboard
  gettingStarted: 'tour-getting-started',

  // Top bar. The help button is where every guide can be replayed from, so the
  // shared quick tour ends by pointing at it.
  helpMenu: 'tour-help-menu',

  // Settings. One route with a tab selector, so guide steps carry the query:
  // /admin/settings?section=general-settings&child=working-hours
  settingsCampusEdit: 'tour-settings-campus-edit',
  settingsWorkingHoursEdit: 'tour-settings-workinghours-edit',
  settingsBillingCenterAdd: 'tour-settings-bc-add',
  settingsTaxPresets: 'tour-settings-tax-presets',
  settingsInvoiceSeriesCreate: 'tour-settings-invoice-series',
  settingsInvoiceTemplateCreate: 'tour-settings-invoice-template',

  // Spaces & seats
  spacesAddNew: 'tour-spaces-add',
  spacePublish: 'tour-space-publish',

  // Plans
  plansCreateNew: 'tour-plans-create',
  planSubmit: 'tour-plan-submit',

  // Team & roles
  teamAddMember: 'tour-team-add',
  rolesNewRole: 'tour-roles-new',

  // Meeting rooms
  meetingRoomCreate: 'tour-mr-create',
  meetingRoomPublicLink: 'tour-mr-publiclink',
  meetingRoomBookingsTab: 'tour-mr-bookings-tab',

  // Branding
  settingsBrandingEdit: 'tour-settings-branding-edit',

  // Integrations. The grid is generic, so the card tags itself by slug.
  integrationRazorpayConnect: 'tour-integration-razorpay',

  // Floor plans. One button per floor, so this matches the first empty floor.
  floorPlanCreate: 'tour-floorplan-create',

  // Members
  membersAddNew: 'tour-members-addnew',
  membersAddMember: 'tour-members-addmember',

  // Orders & invoices
  ordersNewOrder: 'tour-orders-new',
  orderTypeMonthly: 'tour-ordertype-monthly',
  invoicesCreate: 'tour-invoices-create',

  // Front desk
  checkinManual: 'tour-checkin-manual',
  visitorsInvitationsTab: 'tour-visitors-invitations-tab',
  visitorInviteNew: 'tour-visitor-invite-new',
  serviceRequestNew: 'tour-servicerequest-new',

  // Leads
  leadsAddNew: 'tour-leads-add',
  proposalsCreate: 'tour-proposals-create',

  // Renewals
  renewalsWizard: 'tour-renewals-wizard',

  // Operations
  expensesAddNew: 'tour-expenses-add',
  assetsAddNew: 'tour-assets-add',
  inspectNewTemplate: 'tour-inspect-newtemplate',

  // Community
  communityNewPost: 'tour-community-newpost',
  eventsCreate: 'tour-events-create',
};

// Selectors, for the guides.
const sel = (id) => `[data-tour="${id}"]`;

export const ANCHOR = Object.freeze(
  Object.fromEntries(Object.entries(ANCHOR_ID).map(([key, id]) => [key, sel(id)]))
);

// A step with no element to point at: the card centers itself on screen.
export const CENTER = 'body';

export const ALL_ANCHORS = Object.freeze([CENTER, ...Object.values(ANCHOR)]);

export function isKnownAnchor(target) {
  return ALL_ANCHORS.includes(target);
}
